(() => {
  if (customElements.get('hx-cart-coupon')) return;
  const pendingKey = 'hx-pending-discount';
  const normalize = code => String(code || '').trim().toLocaleUpperCase();
  const codesFrom = cart => {
    if (Array.isArray(cart.discount_codes)) return cart.discount_codes.filter(code => code.applicable).map(code => code.code);
    const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))];
    return [...new Set(applications.filter(app => app?.type === 'discount_code').map(app => app.title))];
  };
  const readPending = () => { try { return sessionStorage.getItem(pendingKey) || ''; } catch { return ''; } };
  const savePending = code => { try { if (code) sessionStorage.setItem(pendingKey, code); else sessionStorage.removeItem(pendingKey); } catch {} };
  class HxCartCoupon extends HTMLElement {
    static busy = false;
    connectedCallback() {
      this.events = new AbortController();
      const signal = this.events.signal;
      this.querySelector('details').hidden = false;
      this.querySelectorAll('[data-hx-coupon-remove]').forEach(button => button.hidden = false);
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-hx-coupon-remove]');
        if (remove) this.update('', remove.dataset.hxCouponRemove);
        if (event.target.closest('[data-hx-coupon-apply]')) this.submit();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-hx-coupon-input]')) { event.preventDefault(); this.submit(); }
      }, { signal });
      this.addEventListener('input', () => { this.querySelector('[data-hx-coupon-input]')?.removeAttribute('aria-invalid'); }, { signal });
    }
    disconnectedCallback() { this.events?.abort(); clearTimeout(this.statusTimer); }
    submit() {
      const input = this.querySelector('[data-hx-coupon-input]');
      const code = input.value.trim();
      if (!code) { input.focus(); return; }
      this.update(code);
    }
    message(text, error = false) {
      const node = this.querySelector('[data-hx-coupon-status]');
      node.textContent = text;
      node.dataset.error = String(error);
      clearTimeout(this.statusTimer);
      if (text && !error) this.statusTimer = setTimeout(() => { node.textContent = ''; }, 4000);
      if (error) { this.querySelector('details').open = true; this.querySelector('input').setAttribute('aria-invalid', 'true'); }
    }
    async update(code, remove = '', automatic = false) {
      const Type = this.constructor, drawer = document.querySelector('arte-cart-drawer');
      const Page = customElements.get('arte-cart-page');
      if (Type.busy || drawer?.busy || Page?.busy || customElements.get('arte-cart-subscription')?.busy) return;
      if (!automatic) savePending('');
      Type.busy = true;
      const context = this.dataset.context, labels = { ...this.dataset };
      this.message('');
      const sectionIDs = [...new Set([drawer?.dataset.section, document.querySelector('[data-cart-section]')?.dataset.cartSection].filter(Boolean))];
      const controls = [...document.querySelectorAll('arte-cart-drawer .v3-cart-content button,arte-cart-drawer .v3-cart-content input,[data-cart-section] button,[data-cart-section] input')].map(node => [node, node.disabled]);
      const previousPageBusy = Page?.busy;
      if (Page) Page.busy = true;
      drawer?.lock(true);
      controls.forEach(([node]) => node.disabled = true);
      document.querySelector('[data-cart-section]')?.setAttribute('aria-busy', 'true');
      const blocker = event => { if (event.target.closest('arte-cart-drawer .v3-cart-content a,[data-cart-section] a')) { event.preventDefault(); event.stopImmediatePropagation(); } };
      document.addEventListener('click', blocker, true);
      const currentNode = () => [...document.querySelectorAll('hx-cart-coupon')].find(node => node.dataset.context === context);
      const post = async codes => {
        const response = await fetch(labels.update, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: codes.join(','), sections: sectionIDs, sections_url: location.pathname }) });
        if (!response.ok) throw new Error('discount');
        return response.json();
      };
      const render = async result => {
        let sections = result.sections;
        if (!sections || sectionIDs.some(id => typeof sections[id] !== 'string')) {
          const url = new URL(location.pathname, location.origin);
          url.searchParams.set('sections', sectionIDs.join(','));
          const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error('sections');
          sections = await response.json();
        }
        const pageRendered = customElements.get('arte-cart-page')?.renderSections(sections);
        const drawerRendered = drawer?.renderCart(sections);
        if (!pageRendered && !drawerRendered) throw new Error('sections');
      };
      try {
        const response = await fetch(labels.cart, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('cart');
        const before = await response.json(), originalCodes = codesFrom(before);
        if (automatic && !before.item_count) return;
        const nextCodes = remove ? originalCodes.filter(value => normalize(value) !== normalize(remove)) : [...new Set([...originalCodes.filter(value => normalize(value) !== normalize(code)), code])];
        let result = await post(nextCodes);
        const applied = codesFrom(result).some(value => normalize(value) === normalize(code));
        if (!remove && !applied) {
          result = await post(originalCodes);
          await render(result);
          const target = currentNode();
          if (!automatic && target) { target.querySelector('input').value = code; target.message(labels.invalid, true); target.querySelector('input').focus({ preventScroll: true }); }
          if (!automatic || before.item_count) savePending('');
          return;
        }
        await render(result);
        savePending('');
        const target = currentNode();
        if (target) {
          target.querySelector('details').open = false;
          target.message(remove ? labels.removed : labels.applied);
          if (!automatic) target.querySelector('summary').focus({ preventScroll: true });
        }
      } catch {
        try {
          const url = new URL(location.pathname, location.origin);
          url.searchParams.set('sections', sectionIDs.join(','));
          const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (response.ok) await render({ sections: await response.json() });
        } catch {}
        const target = currentNode();
        if (target && !automatic) { target.querySelector('input').value = code; target.message(labels.network, true); }
      } finally {
        drawer?.lock(false);
        if (Page) Page.busy = previousPageBusy;
        document.querySelector('[data-cart-section]')?.removeAttribute('aria-busy');
        controls.forEach(([node, disabled]) => { if (node.isConnected) node.disabled = disabled; });
        document.removeEventListener('click', blocker, true);
        Type.busy = false;
      }
    }
  }
  customElements.define('hx-cart-coupon', HxCartCoupon);
  let retryTimer;
  const request = (code, attempts = 0) => {
    if (!String(code || '').trim()) return;
    savePending(String(code).trim());
    const node = document.querySelector('arte-cart-drawer hx-cart-coupon') || document.querySelector('hx-cart-coupon');
    if (node) {
      if (HxCartCoupon.busy || document.querySelector('arte-cart-drawer')?.busy || customElements.get('arte-cart-page')?.busy || customElements.get('arte-cart-subscription')?.busy) {
        if (attempts < 40) { clearTimeout(retryTimer); retryTimer = setTimeout(() => { if (readPending() === code) request(code, attempts + 1); }, 250); }
      } else node.update(readPending() || code, '', true);
    }
  };
  document.addEventListener('hx:discount-request', event => request(event.detail?.code));
  document.addEventListener('arte:cart-updated', event => { const code = readPending(); if (code && event.detail?.count > 0 && !HxCartCoupon.busy) request(code); });
  const urlCode = new URLSearchParams(location.search).get('discount');
  if (urlCode) {
    const cleanURL = new URL(location.href);
    cleanURL.searchParams.delete('discount');
    history.replaceState(history.state, '', cleanURL);
    request(urlCode);
  } else if (readPending()) request(readPending());
})();
